import { createFormatFolder, CreateFormatFolderParams, deleteFormat, deleteFormatFolder, getFolderFormats, getFormatFolders } from '@common/actions/formats';
import { FolderFormatsDto, FormatFoldersDto } from '@common/dto/folder-settings';
import { FolderListDto } from '@common/dto/folder/list-dto';
import { FormatManagementControllerProps, SpreadsheetFormatType, SpreadsheetType, SSFormatFoldersDto } from '@modules/spreadsheets/dto/spreadsheet/type';
import { SkipLogicCondition, SkipLogicConditionOperator } from "@common/components/skip-logic/skip-logic.types";
import { I18n } from "react-redux-i18n";
import * as React from 'react';



// Validation function to check for missing values in query conditions
const validateQueryConditions = (conditions: SkipLogicCondition[]): { isValid: boolean; errorMessage?: string } => {
  if (!conditions || conditions.length === 0) {
    return { isValid: true };
  }

  const fieldsWithMissingValues: string[] = [];

  conditions.forEach((condition) => {
    if (condition.operator && 
        condition.operator !== SkipLogicConditionOperator.isBlank && 
        condition.operator !== SkipLogicConditionOperator.isNotBlank &&
        (!condition.value || condition.value.trim() === '')) {
      fieldsWithMissingValues.push(condition.field.name);
    }
  });

  if (fieldsWithMissingValues.length > 0) {
    const baseMessage = I18n.t("The spreadsheet could not be run because a comparison operator is missing a value for the following field(s) in the query. Please fix this error and re-run the spreadsheet");
    const errorMessage = `${baseMessage}: ${fieldsWithMissingValues.join(', ')}`;
    return { isValid: false, errorMessage };
  }

  return { isValid: true };
};

export const useFormatManagementController = ({
  spreadsheetType,
  queryBuilderConditions = [],
  showErrorMessage,
  currentFormat,
}: FormatManagementControllerProps) => {
  const [loadFormatOpened, setLoadFormatOpenedState] = React.useState(false);
  const [saveFormatOpened, setSaveFormatOpenedState] = React.useState(false);
  const [formats, setFormats] = React.useState<Array<SSFormatFoldersDto>>([]);
  const [folders, setFolders] = React.useState<Array<FolderListDto>>([]);

  const resetData = () => {
    setFormats([]);
    setFolders([]);
  };

  // Helper function to recursively find folders with the same name in the same parent
  const findFolderByNameInParent = (folders: FolderListDto[], folderName: string, parentId: number | null): FolderListDto | null => {
    for (const folder of folders) {
      // Check if this folder has the same name and parent
      // Handle null parentId (top-level folders) by checking if parentid is 0 or null
      const folderParentId = folder.parentid === 0 ? null : folder.parentid;
      if (folder.name.trim().toLowerCase() === folderName.trim().toLowerCase() && folderParentId === parentId) {
        return folder;
      }
      
      // Recursively check children
      if (folder.children && folder.children.length > 0) {
        const found = findFolderByNameInParent(folder.children, folderName, parentId);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  // Function to check if a format name already exists in the current folder
  const checkFormatNameExists = React.useCallback((formatName: string, folderId: number): boolean => {
    const name = formatName.trim().toLowerCase();
    const found = formats.some(
      (format) =>
        format.name.trim().toLowerCase() === name &&
        format.folderId === folderId
    );
    return formats.some(
      (format) =>
        format.name.trim().toLowerCase() === name &&
        format.folderId === folderId
    );
  }, [formats]);

  const openLoadFormat = React.useCallback(() => {
    setLoadFormatOpenedState(true);
  }, []);

  const closeLoadFormat = React.useCallback(() => {
    setLoadFormatOpenedState(false);
    resetData();
  }, []);

  const openSaveFormat = React.useCallback(() => {
    // Validate query conditions before opening save format
    if (queryBuilderConditions?.length) {
      const validationResult = validateQueryConditions(queryBuilderConditions);
      if (!validationResult.isValid) {
        showErrorMessage(validationResult.errorMessage!);
        return;
      }
    }
    setSaveFormatOpenedState(true);
  }, [queryBuilderConditions, showErrorMessage]);


  const closeSaveFormat = React.useCallback(() => {
    setSaveFormatOpenedState(false);
    resetData();
  }, []);

  const handleFormatDelete = React.useCallback(async (folderId: number, formatId: number) => {
    try {
      await deleteFormat(spreadsheetType === SpreadsheetType.individual ? SpreadsheetFormatType.defaultIndividual : SpreadsheetFormatType.defaultPedigree, {formatId});
      // Refresh formats for the current folder after deletion
      const data = await getFolderFormats(spreadsheetType === SpreadsheetType.individual ? SpreadsheetFormatType.defaultIndividual : SpreadsheetFormatType.defaultPedigree, { folderId });
      
      // Handle both possible response structures
      if (data && typeof data === 'object') {
        let formatsArray: Array<SSFormatFoldersDto> = [];
        
        if (data.formats) {
          formatsArray = data.formats;
        } else if (Array.isArray(data)) {
          formatsArray = data;
        }
        
        // Sort formats alphabetically by name
        const sortedFormats = [...formatsArray].sort((a, b) => {
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        setFormats(sortedFormats);
      } else {
        setFormats([]);
      }
    } catch (error) {
      console.error('Error deleting format:', error);
      throw error;
    }
  }, [spreadsheetType]);

  const getAndSetFolders = React.useCallback(async () => {
    try {
      const data = await getFormatFolders(spreadsheetType === SpreadsheetType.individual ? SpreadsheetFormatType.defaultIndividual : SpreadsheetFormatType.defaultPedigree);
      // Handle both possible response structures
      if (data && typeof data === 'object') {
        if (data.formatFolders) {
          setFolders(data.formatFolders);
        } else if (Array.isArray(data)) {
          setFolders(data);
        } else {
          setFolders([]);
        }
      } else {
        setFolders([]);
      }
    } catch (error) {
      console.error('Error getting folders:', error);
      setFolders([]);
    }
  }, [spreadsheetType]);

  // Load folders when load format window opens
  React.useEffect(() => {
    if (loadFormatOpened) {
      getAndSetFolders();
    }
  }, [loadFormatOpened, getAndSetFolders]);

  // Load folders when save format window opens
  React.useEffect(() => {
    if (saveFormatOpened) {
      getAndSetFolders();
    }
  }, [saveFormatOpened, getAndSetFolders]);

  const handleFolderCreate = React.useCallback(async (payload: CreateFormatFolderParams) => {
    try {
      // Check if folder name already exists in the same parent folder
      const existingFolder = findFolderByNameInParent(folders, payload.name, payload.parentId);
      
      if (existingFolder) {
        const errorMessage = I18n.t('The folder {folderName} already exists. Please enter a different name', { folderName: payload.name });
        showErrorMessage(errorMessage);
        return;
      }

      await createFormatFolder(spreadsheetType === SpreadsheetType.individual ? SpreadsheetFormatType.defaultIndividual : SpreadsheetFormatType.defaultPedigree, {name: payload.name, parentId: payload.parentId});
      // Refresh folders after creating a new folder
      await getAndSetFolders();
    } catch (error) {
      console.error('Error creating folder:', error);
      throw error;
    }
  }, [spreadsheetType, getAndSetFolders, folders, showErrorMessage]);

  const handleFolderDelete = React.useCallback(async (folderId: number) => {
    try {
      await deleteFormatFolder(spreadsheetType === SpreadsheetType.individual ? SpreadsheetFormatType.defaultIndividual : SpreadsheetFormatType.defaultPedigree, {folderId});
      // Refresh folders after deleting a folder
      await getAndSetFolders();
    } catch (error) {
      console.error('Error deleting folder:', error);
      throw error;
    }
  }, [spreadsheetType, getAndSetFolders]);

  const getAndSetFormats = React.useCallback(async (folderId: number) => {
    const data = await getFolderFormats(spreadsheetType === SpreadsheetType.individual ? SpreadsheetFormatType.defaultIndividual : SpreadsheetFormatType.defaultPedigree, { folderId });
    // Handle both possible response structures
    if (data && typeof data === 'object') {
      let formatsArray: Array<SSFormatFoldersDto> = [];
      
      if (data.formats) {
        formatsArray = data.formats;
      } else if (Array.isArray(data)) {
        formatsArray = data;
      }
      const sortedFormats = [...formatsArray].sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setFormats(sortedFormats);
    } else {
      setFormats([]);
    }
  }, [spreadsheetType]);

  const handleFormatsReset = React.useCallback(() => {
    setFormats([]);
  }, []);

  const handleFolderSelect = React.useCallback(async (folderId: number) => {
    await getAndSetFormats(folderId);
  }, [getAndSetFormats]);

  return {
    openSaveFormat,
    closeSaveFormat,
    saveFormatOpened,
    formats,
    folders,
    getAndSetFolders,
    getAndSetFormats,
    handleFormatsReset,
    handleFolderDelete,
    handleFolderCreate,
    handleFormatDelete,
    loadFormatOpened,
    openLoadFormat,
    closeLoadFormat,
    currentFormat,
    checkFormatNameExists,
    handleFolderSelect,
  };
};